import { useEffect, useState } from 'react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { TOOLS } from '../constants';
import { readImagePlacements } from '../engine/imageSwap';
import { usePdfEditorStore } from '../state/usePdfEditorStore';

const NONE = {};

/**
 * Where every picture on every page is painted, for the "replace image" tool.
 *
 * Read once, the first time the tool is armed, and then kept: the answer comes
 * from the SOURCE document, which never changes, so re-reading it on a tool
 * toggle would be a second parse of the whole file for the same result.
 */
export function useImagePlacements() {
    const { t } = useTranslation();
    const sourceBytes = usePdfEditorStore((s) => s.sourceBytes);
    const pages = usePdfEditorStore((s) => s.pages);
    const armed = usePdfEditorStore((s) => s.activeTool === TOOLS.REPLACE_IMAGE);

    const [placements, setPlacements] = useState(NONE);

    useEffect(() => {
        if (!armed || !sourceBytes || placements !== NONE) return undefined;
        let alive = true;
        readImagePlacements(sourceBytes, pages)
            .then((found) => {
                if (!alive) return;
                setPlacements(found);
                // Arming a tool that then highlights nothing reads as a broken
                // tool. Say which it is.
                if (!Object.values(found).some((list) => list.length)) {
                    notifications.show({
                        title: t('pdfEditor.tools.replaceImage'),
                        message: t('pdfEditor.images.noImages'),
                        color: 'yellow',
                    });
                }
            })
            // A file this cannot parse simply offers no pictures to replace —
            // the tool then finds nothing to click, which is the honest result.
            .catch(() => { if (alive) setPlacements({}); });
        return () => { alive = false; };
    }, [armed, sourceBytes, pages, placements, t]);

    useEffect(() => { setPlacements(NONE); }, [sourceBytes]);

    return placements;
}
